import { useQuery } from "@tanstack/react-query";

import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";
import { useUser } from "util/UserContext";
import type { Actor, ActorRef } from "types/actor";

/**
 * Single backend row for the active-stories rail. Mirrors
 * `UserStoryRailEntry` in `UserStoryAvatar.tsx` but does not depend on
 * that file (this hook is the lower-level primitive). Only the fields
 * used here are typed; extra fields from the API are ignored.
 */
interface ActiveStoryUserRow {
  user_id: number;
  has_unseen: boolean;
}

interface ActiveStoryRailResponse {
  items: ActiveStoryUserRow[];
  scope: "all" | "following";
}

/**
 * Single shared TanStack query key for the "all active story owners"
 * fetch. Used by:
 *   - `useActorStoryStatus(actor)` (this hook), so every avatar on the
 *     page resolves story state from the same in-memory list.
 *   - The home rail (eventually) when it switches to `scope=all`. For
 *     the time being the rail still uses its own `following`-scoped
 *     query, but anything that does not need follow-graph filtering
 *     should pull from this cache.
 *
 * Keeping the key stable across the app means we make at most one
 * network round-trip per session per refresh interval, even when the
 * page renders hundreds of avatars.
 */
export const ACTOR_STORY_STATUS_QUERY_KEY = ["stories", "activeUsers", "all"] as const;

const REFRESH_INTERVAL_MS = 60 * 1000;

interface ActorStoryStatus {
  /** True when the actor has at least one active story right now. */
  hasStory: boolean;
  /**
   * True when the actor has at least one active story the viewer has
   * not seen yet. Always `false` for anonymous viewers (the backend
   * cannot compute "unseen" without a session, and showing every actor
   * as "unseen" is misleading per ADR 010).
   */
  hasUnseen: boolean;
  /** True while the underlying rail query is still loading. */
  isLoading: boolean;
}

/**
 * Resolve the current actor's "has active story" + "has unseen story"
 * state from the shared rail cache. Returns sensible defaults
 * (`hasStory: false`, `hasUnseen: false`) when the actor is null, when
 * the actor is a Partea (no team stories yet), or when the viewer is
 * anonymous.
 *
 * This is the hook every avatar in the product calls to decide whether
 * to draw a ring around itself. Designed to be cheap to call from many
 * places — they all share one network query and one in-memory cache.
 */
export function useActorStoryStatus(actor: ActorRef): ActorStoryStatus {
  const { currentUser, awaitingUser } = useUser();
  const isSignedIn = Boolean(currentUser?.id);
  const enabled = isSignedIn && !awaitingUser && isUserActor(actor);

  const railQuery = useQuery<ActiveStoryRailResponse>({
    queryKey: ACTOR_STORY_STATUS_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<ActiveStoryRailResponse>(
        apiRoutes.entities.placementsActiveUsers,
        { params: { scope: "all" } }
      );
      return response.data;
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  if (!actor || actor.kind !== "user") {
    return { hasStory: false, hasUnseen: false, isLoading: false };
  }

  const items = railQuery.data?.items ?? [];
  const match = items.find((row) => row.user_id === actor.id);

  return {
    hasStory: Boolean(match),
    // Anonymous viewers cannot compute unseen state, so we never show
    // the "new content" gradient ring for them.
    hasUnseen: isSignedIn && Boolean(match?.has_unseen),
    isLoading: enabled && railQuery.isLoading,
  };
}

/**
 * Type guard ensuring the actor is a user with a positive numeric id.
 * Teams (Parteas) currently cannot have stories, so we short-circuit
 * the query for them.
 */
function isUserActor(actor: ActorRef): actor is Actor {
  if (!actor || actor.kind !== "user") {
    return false;
  }
  return Number.isFinite(actor.id) && actor.id > 0;
}
